import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal } from '@els/els-react--modal';
import { Paginator } from '@els/els-react--paginator';
import { SingleSelect } from '@els/els-react--select';
import { ErrorBoundary } from '@els/els-react--utils';
import isNaN from 'lodash/isNaN';
import {
  AGGREGATOR_MODAL_MAX_PAGES_TO_SHOW,
  AGGREGATOR_MODAL_PAGE_SIZE,
  AGGREGATOR_MODAL_WIDTH,
  AGGREGATOR_PRODUCT_INFORMATION_TABLE_CAPTION,
} from './constants';
import { buildProductInformationRows } from './SubstanceCommercialSupplierInfo.helpers';

import './SubstanceCommercialSupplierAggregatorModal.scss';

const productInformationRowShape = PropTypes.shape({
  identificationNumber: PropTypes.string,
  productName: PropTypes.string,
  supplierName: PropTypes.string,
});

function SupplierProductRows({ rows, shouldShowSupplierColumn }) {
  return (
    <tbody className="rcs-commercial-aggregator-modal__body">
      {rows.map((row) => (
        <tr
          key={`${row.supplierName}-${row.identificationNumber}-${row.productName}`}
          className="rcs-commercial-aggregator-modal__row no-hover-background">
          {shouldShowSupplierColumn && <td className="rcs-commercial-aggregator-modal__cell">{row.supplierName || ''}</td>}
          <td className="rcs-commercial-aggregator-modal__cell">{row.identificationNumber || ''}</td>
          <td className="rcs-commercial-aggregator-modal__cell">{row.productName || ''}</td>
        </tr>
      ))}
    </tbody>
  );
}

SupplierProductRows.propTypes = {
  rows: PropTypes.arrayOf(productInformationRowShape).isRequired,
  shouldShowSupplierColumn: PropTypes.bool.isRequired,
};

function SubstanceCommercialSupplierAggregatorModal({ group, onClose }) {
  const [currentPage, setCurrentPage] = useState(1);
  const productInformationRows = useMemo(() => buildProductInformationRows(group), [group]);
  const shouldShowSupplierColumn = Boolean(group?.isAggregator);
  const modalTitleId = `rcs-commercial-aggregator-title-${group.supplierId}`;
  const modalTitle = `Product Information from ${group.supplierName}`;
  const totalPages = Math.ceil(productInformationRows.length / AGGREGATOR_MODAL_PAGE_SIZE);
  const pageOptions = useMemo(
    () =>
      Array.from({ length: totalPages }, (_, index) => {
        const page = index + 1;

        return {
          label: `${page}`,
          displayValue: `${page}`,
          value: page,
        };
      }),
    [totalPages]
  );

  const paginatedRows = useMemo(() => {
    const startIndex = (currentPage - 1) * AGGREGATOR_MODAL_PAGE_SIZE;
    const endIndex = startIndex + AGGREGATOR_MODAL_PAGE_SIZE;
    return productInformationRows.slice(startIndex, endIndex);
  }, [productInformationRows, currentPage]);

  const handlePageChange = (value) => {
    const page = parseInt(value, 10);

    if (!isNaN(page) && page > 0 && page <= totalPages && page !== currentPage) {
      setCurrentPage(page);
    }
  };

  const handleGoToPageSelect = (option) => {
    handlePageChange(option?.value);
  };

  const handleResultsPerPageChange = () => AGGREGATOR_MODAL_PAGE_SIZE;

  const resultsPerPageOption = useMemo(
    () => ({ label: `${AGGREGATOR_MODAL_PAGE_SIZE}`, displayValue: `${AGGREGATOR_MODAL_PAGE_SIZE}`, value: AGGREGATOR_MODAL_PAGE_SIZE }),
    []
  );

  return (
    <Modal
      onClose={onClose}
      className="rcs-commercial-aggregator-modal"
      id={`rcs-commercial-aggregator-${group.supplierId}`}
      label={modalTitle}
      width={AGGREGATOR_MODAL_WIDTH}>
      <header className="rcs-commercial-aggregator-modal__header">
        <h2 id={modalTitleId} className="rcs-commercial-aggregator-modal__title">
          {modalTitle}
        </h2>
      </header>

      <section className="rcs-commercial-aggregator-modal__content">
        <div className="rcs-commercial-aggregator-modal__table-wrapper">
          <table className="rx-table rx-table--full-width rcs-commercial-aggregator-modal__table c-els-table c-els-table--vertical-dividers c-els-table--subtle-dividers">
            <caption className="u-els-hide-visually">{AGGREGATOR_PRODUCT_INFORMATION_TABLE_CAPTION}</caption>
            <thead className="rcs-commercial-aggregator-modal__head">
              <tr className="rcs-commercial-aggregator-modal__head-row">
                {shouldShowSupplierColumn && (
                  <th scope="col" className="rcs-commercial-aggregator-modal__head-cell">
                    Supplier
                  </th>
                )}
                <th scope="col" className="rcs-commercial-aggregator-modal__head-cell">
                  Identification number:
                </th>
                <th scope="col" className="rcs-commercial-aggregator-modal__head-cell">
                  Product name
                </th>
              </tr>
            </thead>
            <SupplierProductRows rows={paginatedRows} shouldShowSupplierColumn={shouldShowSupplierColumn} />
          </table>
        </div>

        {productInformationRows.length > AGGREGATOR_MODAL_PAGE_SIZE && (
          <div className="rcs-commercial-aggregator-modal__pagination">
            <div className="rcs-commercial-aggregator-modal__results-per-page-control">
              <span className="rcs-commercial-aggregator-modal__pagination-label">Results per page</span>
              <SingleSelect
                className="rcs-commercial-aggregator-modal__results-per-page"
                data-e2e="product-information-results-per-page"
                options={[resultsPerPageOption]}
                selectedOptions={[AGGREGATOR_MODAL_PAGE_SIZE]}
                label="Results per page"
                shouldHideLabel
                onSelect={handleResultsPerPageChange}
              />
            </div>
            <ErrorBoundary>
              <Paginator
                className="rcs-commercial-aggregator-modal__paginator"
                onPageChange={handlePageChange}
                resultsPerPage={AGGREGATOR_MODAL_PAGE_SIZE}
                totalResults={productInformationRows.length}
                currentPage={currentPage}
                shouldShowFirstLastButton
                maxPagesToShow={AGGREGATOR_MODAL_MAX_PAGES_TO_SHOW}
                a11y={{
                  name: `Product information pagination for ${group.supplierName}`,
                  pagingUnitName: 'Page',
                  morePagingUnit: 'More pages',
                }}
              />
            </ErrorBoundary>
            <SingleSelect
              className="rcs-commercial-aggregator-modal__go-to-page"
              data-e2e="product-information-go-to-page"
              options={pageOptions}
              selectedOptions={[]}
              label="Go to page"
              placeholderText="Go to page"
              onSelect={handleGoToPageSelect}
              shouldHideLabel
            />
          </div>
        )}
      </section>
    </Modal>
  );
}

SubstanceCommercialSupplierAggregatorModal.propTypes = {
  group: PropTypes.shape({
    aggregatorRows: PropTypes.arrayOf(PropTypes.object),
    isAggregator: PropTypes.bool,
    offers: PropTypes.arrayOf(PropTypes.object),
    supplierId: PropTypes.string.isRequired,
    supplierName: PropTypes.string.isRequired,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
};

export default SubstanceCommercialSupplierAggregatorModal;
